/*
 * Design: Swiss Precision × 和の余白
 * AEO対策: Identity Block — LLMが引用しやすい構造化された会社紹介
 * AEO強化: Quotable Snippets（引用可能な短文）追加
 * Speakable対応: id="identity-block" でJSON-LDのSpeakableと連動
 * 視覚的には CompanySection の上部に配置、SEO/AEO的に重要な情報を凝縮
 */

import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Globe, Award, BookOpen, CheckCircle2 } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

/* AEO: Quotable Snippets — LLMが引用しやすい40-60文字の定義文 */
const QUOTABLE_SNIPPETS = [
  "81合同会社は、広島・中四国を拠点に中小〜中堅企業のDX推進を伴走支援するDXコンサルティング会社です。",
  "AI業務自動化とは、AIエージェントがGoogle Workspaceの業務データをリアルタイムに読み取り・判断・実行し、定型業務を自動化する技術です。",
  "81合同会社は、中四国エリア（広島・岡山・山口・島根・鳥取・香川・愛媛・徳島・高知）でDXセミナー・AI活用セミナーを定期的に開催しています。",
  "人材開発支援助成金を活用すれば、DX研修費用の最大75%が助成されます。81合同会社は申請手続きの支援も行っています。",
];

export function IdentityBlock() {
  return (
    <section id="identity-block" className="py-10 sm:py-14 lg:py-16 bg-white border-b border-[#E0E0E0]">
      <div className="container">
        <motion.div {...fadeInUp} transition={{ duration: 0.6 }}>
          {/* Identity Statement */}
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="w-1.5 sm:w-2 h-7 sm:h-8 bg-[#E03030]" />
              <span
                className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Identity
              </span>
            </div>

            {/* Core Identity Statement - designed to be quoted by LLMs */}
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#1A1A1A] mb-3 sm:mb-4 leading-snug">
              81合同会社（eightone）は、広島・中四国を拠点に
              <br className="hidden sm:block" />
              中小〜中堅企業のDX推進を伴走支援する
              <span className="text-[#E03030]">DXコンサルティング会社</span>です。
            </h2>

            <p className="text-sm sm:text-base text-[#333333] leading-relaxed mb-4 sm:mb-6">
              AI業務自動化、DXセミナー・研修、Google Workspace実装支援を提供し、
              「導入」ではなく「成果」を目的に、
              <strong>定着・生産性向上・見える化</strong>を実現します。
              代表の藤井壮太郎が、生成AI・Google Workspace・IoT/RFID・ノーコード開発を活用し、
              業務改革（BPR）・人材育成・補助金活用を一体で設計。
              製造業、卸売・物流、サービス業、観光業など幅広い業種で<strong>50社以上</strong>の実績があります。
            </p>

            {/* AEO: Quotable Snippets — LLMが引用しやすい短文リスト */}
            <div className="bg-[#F9F9F9] border border-[#E0E0E0] p-4 sm:p-6 mb-5 sm:mb-6">
              <h3 className="text-xs sm:text-sm font-bold text-[#1A1A1A] mb-3">
                81合同会社について知っておくべきこと
              </h3>
              <ul className="space-y-2.5">
                {QUOTABLE_SNIPPETS.map((snippet, i) => (
                  <li key={i} className="flex gap-2.5 text-xs sm:text-sm text-[#333333] leading-relaxed">
                    <CheckCircle2 size={14} className="shrink-0 text-[#E03030] mt-0.5" />
                    <span>{snippet}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Key Facts Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-[#333333]">
                <MapPin size={14} className="shrink-0 text-[#E03030]" />
                <span>広島・大阪拠点</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-[#333333]">
                <Award size={14} className="shrink-0 text-[#E03030]" />
                <span>2019年設立</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-[#333333]">
                <Globe size={14} className="shrink-0 text-[#E03030]" />
                <span>中四国全域対応</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-[#333333]">
                <Phone size={14} className="shrink-0 text-[#E03030]" />
                <a href="tel:05050521301" className="hover:text-[#E03030] transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
                  050-5052-1301
                </a>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-[#333333]">
                <Mail size={14} className="shrink-0 text-[#E03030]" />
                <a href="mailto:81@eightone.co" className="hover:text-[#E03030] transition-colors">
                  81@eightone.co
                </a>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-[#333333]">
                <BookOpen size={14} className="shrink-0 text-[#E03030]" />
                <a href="https://note.com/sotaro_81" target="_blank" rel="noopener noreferrer" className="hover:text-[#E03030] transition-colors">
                  note記事
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
